import type * as ecModels from '../../../models'

import * as models from './models'

/**
 * {@link https://ngrx.io/docs State} of this plugin
 */
export interface State<
  TTypeParams extends ecModels.TypeParams = ecModels.TypeParams,
> {
  /**
   * @see {@link models.EditedEntity.Lookup}
   *
   * REVIEW: Value of a lookup might not be typed correctly but this is what
   * {@link models.Edit.Object.input} allows us to do.
   */
  creationsLookup: Readonly<
    models.EditedEntity.Lookup<
      models.CreationId,
      // TODO: Figure out how to deal with the fact that user might start creation
      // with non of the required properties filled out (of the TCreateEntity),
      // but they have to fill out all the required properties before they can
      // submit
      Partial<TTypeParams['createEntity']>
    >
  >
  /**
   * @see {@link models.EditedEntity.Lookup}
   */
  updatesLookup: Readonly<
    models.EditedEntity.Lookup<ecModels.EntityId, TTypeParams['entity']>
  >
  /**
   * Set of {@link ecModels.EntityId} that are marked for deletion,
   */
  deletions: ReadonlySet<ecModels.EntityId>
  isSubmitting: boolean
  submissionError?: string | Error
}
export namespace State {
  // NOTE: State<any> ensures this can be used for any State
  export const INITIAL: State<any> = {
    creationsLookup: {},
    deletions: new Set(),
    isSubmitting: false,
    updatesLookup: {},
  }

  /**
   * Handles a successful submission by removing the applied changes from the state
   * and marking the submission as complete.
   */
  export function onSuccess<TTypeParams extends ecModels.TypeParams>(
    selection: Readonly<models.EditsSelection.Result<TTypeParams>>,
    state: Readonly<State<TTypeParams>>,
  ): Readonly<State> {
    if (models.EditsSelection.Result.isEmpty(selection)) {
      // Nothing to update, but ensure submission state is reset
      return { ...state, isSubmitting: false, submissionError: undefined }
    }

    const { creations, updates, deletions } = selection

    /**
     * A reference to {@link state} that is {@link Mutable}
     */
    const stateRef: Mutable<typeof state> = { ...state }

    // TODO: find a way to make this DRY

    if (creations.length) {
      /**
       * A reference to {@link State.creationsLookup} that is {@link Mutable}
       */
      const creationsLookup: Mutable<typeof state.creationsLookup> = {
        ...state.creationsLookup,
      }
      for (const { id } of creations) {
        delete creationsLookup[id]
      }
      stateRef.creationsLookup = creationsLookup
    }

    if (updates.length) {
      /**
       * A reference to {@link State.updatesLookup} that is {@link Mutable}
       */
      const updatesLookup: Mutable<typeof state.updatesLookup> = {
        ...state.updatesLookup,
      }
      for (const { id } of updates) {
        delete updatesLookup[id]
      }
      stateRef.updatesLookup = updatesLookup
    }

    if (deletions.length) {
      /**
       * A reference to {@link State.deletions} that is {@link Mutable}
       */
      const updatedDeletions = new Set(state.deletions)
      for (const id of deletions) {
        updatedDeletions.delete(id)
      }
      stateRef.deletions = updatedDeletions
    }

    // Mark submission as completed
    stateRef.isSubmitting = false
    stateRef.submissionError = undefined

    return stateRef
  }

  /**
   * Marks the beginning of a submission process.
   */
  export function onRequest<TTypeParams extends ecModels.TypeParams>(
    state: Readonly<State<TTypeParams>>,
  ): Readonly<State> {
    return {
      ...state,
      isSubmitting: true,
      submissionError: undefined,
    }
  }

  /**
   * Handles a failed submission.
   */
  export function onError<TTypeParams extends ecModels.TypeParams>(
    err: Error,
    state: Readonly<State<TTypeParams>>,
  ): Readonly<State> {
    return {
      ...state,
      isSubmitting: false,
      submissionError: err,
    }
  }

  /**
   * Handles a cancelled submission.
   */
  export function onCancellation<TTypeParams extends ecModels.TypeParams>(
    state: Readonly<State<TTypeParams>>,
  ): Readonly<State> {
    return {
      ...state,
      isSubmitting: false,
    }
  }
}
